import { IPortfolio } from "@portal/types/api/IPortfolio";
import useSWR, { SWRResponse } from "swr";
import { IUsePortfolio } from "./types";
import { useMemo } from "react";
import commonSwrConfig from "../commonSwrConfig";
import useCenterId from "../useCenterId";
import { NORNE_CENTERID } from "@portal/assets/banks";
import { useSession } from "@portal/auth/client/SessionProvider";
import { commonFetcher } from "../common/commonFetcher";

const usePortfolios: IUsePortfolio = options => {
  const { update } = useSession();
  const centerId = useCenterId();

  const { data, error, isLoading, mutate }: SWRResponse<IPortfolio[]> = useSWR(
    `/api/portfolio?includeInactive=${options?.includeInactive ?? false}`,
    commonFetcher<IPortfolio[]>,
    options?.errorRetryCount !== undefined
      ? { ...commonSwrConfig, errorRetryCount: options.errorRetryCount }
      : commonSwrConfig(update),
  );

  const mainPortfolio = useMemo(() => {
    if (!data || data.length === 0) return undefined;
    return data.reduce((prev, next) => (+prev.portfolioId < +next.portfolioId || next.taxationType === "ASK" ? prev : next));
  }, [data]);

  const bankPortfolios = useMemo(
    () => (centerId === NORNE_CENTERID ? data : data?.filter(portfolio => portfolio.centerId === centerId)),
    [data, centerId],
  );

  const mainBankPortfolio = useMemo(() => {
    if (!bankPortfolios || bankPortfolios.length === 0) return undefined;
    return bankPortfolios.reduce((prev, next) =>
      +prev.portfolioId < +next.portfolioId || next.taxationType === "ASK" ? prev : next,
    );
  }, [bankPortfolios]);

  const isBankPortfolio = (portfolioId?: string): boolean => {
    if (!bankPortfolios || !portfolioId) return false;
    return bankPortfolios.some(p => p.portfolioId === portfolioId);
  };

  const updatePortfolio = async (portfolioId: string, portfolioName: string) => {
    if (portfolioId === undefined || portfolioName === undefined) return;
    await fetch(`/api/portfolio/${portfolioId}`, {
      method: "PUT",
      headers: [["content-type", "application/json"]],
      body: JSON.stringify({ portfolioName }),
    });

    const portfolio = data?.find(portfolio => portfolio.portfolioId === portfolioId);
    if (data && portfolio) {
      portfolio.name = portfolioName;
      let updatedPortfolioList = [...data];
      updatedPortfolioList = updatedPortfolioList.filter(portfolio => portfolio.portfolioId !== portfolioId);
      updatedPortfolioList.push(portfolio);
      mutate(updatedPortfolioList);
    }
  };

  return {
    data,
    bankPortfolios,
    isBankPortfolio,
    isLoading,
    error,
    mutate,
    updatePortfolio,
    mainPortfolio,
    mainBankPortfolio,
  };
};

export default usePortfolios;
